import type { ReactNode } from "react";
import { Calendar, Users, Sparkles, Phone, MessageCircle } from "lucide-react";
import Badge from "@/app/components/Badge";
import ActionMenu, { type ActionMenuItem } from "@/app/components/ActionMenu";
import { formatDate } from "@/lib/data";
import { enquiryName, enquiryPhone, latestQuotation, statusLabel, telHref, whatsappHref, type EnquiryData } from "../enquiry";
import type { PrimaryAction } from "./EnquiryTable";

interface EnquiryCardProps {
  enquiries: EnquiryData[];
  onOpen: (id: number) => void;
  primaryAction: (e: EnquiryData) => PrimaryAction | null;
  menuItems: (e: EnquiryData) => ActionMenuItem[];
  emptyState: ReactNode;
}

/** Phone layout: one tappable card per enquiry */
export default function EnquiryCard({ enquiries, onOpen, primaryAction, menuItems, emptyState }: EnquiryCardProps) {
  return (
    <div className="md:hidden bg-white rounded-xl border border-stone-200 overflow-hidden mb-4">
      {enquiries.length === 0 ? emptyState : (
        <div className="divide-y divide-stone-100">
          {enquiries.map((e) => {
            const name = enquiryName(e);
            const phone = enquiryPhone(e);
            const tel = telHref(phone);
            const wa = whatsappHref(phone);
            const primary = primaryAction(e);
            const quote = latestQuotation(e);
            return (
              <div key={e.id} className={`p-4 ${e.status === "rejected" ? "opacity-70" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <button type="button" onClick={() => onOpen(e.id)} className="min-w-0 text-left flex-1" aria-label={`Open enquiry from ${name}`}>
                    <p className="font-semibold text-stone-800 break-words">{name}</p>
                    <p className="text-xs text-stone-500 mt-0.5 truncate">
                      {[phone, e.created_at ? `Received ${formatDate(e.created_at)}` : ""].filter(Boolean).join(" · ")}
                    </p>
                  </button>
                  <div className="text-right flex-shrink-0">
                    <Badge label={statusLabel(e.status)} status={e.status} />
                    {quote && <p className="text-[11px] text-stone-400 mt-0.5 font-mono">{quote.quotation_number || `#${quote.id}`}</p>}
                  </div>
                </div>
                <button type="button" onClick={() => onOpen(e.id)} className="mt-3 w-full grid grid-cols-2 gap-2 text-xs text-left" tabIndex={-1} aria-hidden="true">
                  <span className="flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2 min-w-0">
                    <Calendar className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                    <span className="truncate">{e.event_date ? formatDate(e.event_date) : "No date"}</span>
                  </span>
                  <span className="flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2 min-w-0">
                    <Users className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                    <span className="truncate">{e.guest_count ? `${e.guest_count} guests` : "Guests not given"}</span>
                  </span>
                  <span className="col-span-2 flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2 min-w-0">
                    <Sparkles className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                    <span className="truncate">{e.type || "Event type not given"}</span>
                  </span>
                </button>
                <div className="mt-3 flex items-center gap-2">
                  {tel && (
                    <a href={tel} className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50" aria-label={`Call ${name}`} title="Call">
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                  {wa && (
                    <a href={wa} target="_blank" rel="noopener noreferrer" className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-emerald-600 hover:bg-emerald-50" aria-label={`WhatsApp ${name}`} title="WhatsApp">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <div className="flex-1" />
                  {primary && (
                    <button
                      type="button"
                      onClick={primary.onClick}
                      className="h-10 px-4 rounded-lg text-sm font-semibold whitespace-nowrap text-white bg-amber-500 hover:bg-amber-600 transition-colors"
                    >
                      {primary.label}
                    </button>
                  )}
                  <ActionMenu items={menuItems(e)} label={`More actions for ${name}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
